import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { useLanguage } from '../../App';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, Heart, Utensils, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PetAlerts() {
  const { token } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const isEN = language === 'en';
  const [alerts, setAlerts] = useState([]);
  const [dismissed, setDismissed] = useState(() => {
    const saved = sessionStorage.getItem('pantass_pet_alerts_dismissed');
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    if (!token) return;
    fetch(`${BACKEND_URL}/api/pet/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data?.has_pet) return;
        const newAlerts = [];
        if (data.hunger < 20) {
          newAlerts.push({
            id: 'hungry',
            icon: Utensils,
            color: '#F97316',
            text: isEN ? `${data.pet_name} is very hungry! Feed them now.` : `${data.pet_name} sangat lapar! Beri makan sekarang.`,
            action: () => navigate('/pet'),
          });
        }
        if (data.happiness < 20) {
          newAlerts.push({
            id: 'sad',
            icon: Heart,
            color: '#f43f5e',
            text: isEN ? `${data.pet_name} is sad! Play a quiz to cheer them up.` : `${data.pet_name} sedih! Main kuiz untuk gembirakan dia.`,
            action: () => navigate('/'),
          });
        }
        setAlerts(newAlerts.filter(a => !dismissed.includes(a.id)));
      })
      .catch(() => {});
  }, [token]);

  const dismiss = (id) => {
    const newDismissed = [...dismissed, id];
    setDismissed(newDismissed);
    sessionStorage.setItem('pantass_pet_alerts_dismissed', JSON.stringify(newDismissed));
    setAlerts(prev => prev.filter(a => a.id !== id));
  };

  if (alerts.length === 0) return null;

  return (
    <div data-testid="pet-alerts" style={{ marginBottom: 12 }}>
      <AnimatePresence>
        {alerts.map(alert => {
          const Icon = alert.icon;
          return (
            <motion.div
              key={alert.id}
              data-testid={`pet-alert-${alert.id}`}
              initial={{ opacity: 0, y: -10, height: 0 }}
              animate={{ opacity: 1, y: 0, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              onClick={alert.action}
              style={{
                background: `${alert.color}15`, border: `1px solid ${alert.color}30`,
                borderRadius: 12, padding: '10px 14px', marginBottom: 6,
                display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
                position: 'relative',
              }}
            >
              <div style={{ width: 28, height: 28, borderRadius: 8, background: `${alert.color}25`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon size={14} color={alert.color} />
              </div>
              <span style={{ fontSize: 13, color: alert.color, fontWeight: 600, flex: 1 }}>{alert.text}</span>
              <button onClick={e => { e.stopPropagation(); dismiss(alert.id); }} style={{ background: 'none', border: 'none', color: '#475569', cursor: 'pointer', padding: 2 }}>
                <X size={12} />
              </button>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}
